<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useQuery } from "@urql/vue";
import { gql } from "@urql/core";
import { IconCheck, IconLoader2, IconStack2 } from "@tabler/icons-vue";
import OverlayModal from "@/components/common/OverlayModal.vue";
import { userData } from "@/init";
import {
  describeFlowShareViolation,
  firstBlockingFlowShareViolation,
  flowShareExistingSpaceNames,
  flowShareSucceeded,
  useFlowSpaceShareMutation,
} from "@/composables/useFlowSpaceShare";
import {
  FlowPickerSpaceDocument,
  type FlowPickerSpaceQuery,
} from "./flowPickerSpaceQuery";

const props = defineProps<{
  spaceId: string;
  sectionId: string;
  spaceName?: string;
}>();

const emit = defineEmits<{
  close: [];
  added: [];
}>();

const { t } = useI18n();

// ── Own custom flows ──
const FlowPickerFlowsDocument = gql`
  query FlowPickerFlows($creatorId: BigInt!) {
    flows(
      filter: { creatorId: { equalTo: $creatorId } }
      orderBy: UPDATED_AT_DESC
    ) {
      nodes {
        id
        title
        flowItems {
          totalCount
        }
        firstItem: flowItems(first: 1, orderBy: [ORDER_ASC]) {
          nodes {
            page {
              document {
                thumbnailDownloadUrl
              }
            }
            video {
              thumbnailDownloadUrl
            }
          }
        }
      }
    }
  }
`;

const {
  data: flowsData,
  fetching: flowsFetching,
  error: flowsError,
} = useQuery<{
  flows: { nodes: any[] };
}>({
  query: FlowPickerFlowsDocument,
  variables: computed(() => ({ creatorId: userData.value?.postgresId ?? "0" })),
  requestPolicy: "cache-and-network",
  pause: computed(() => !userData.value?.postgresId),
});

interface FlowRow {
  id: string;
  title: string;
  thumbnail: string | null;
  pages: number;
}

const flows = computed<FlowRow[]>(() => {
  const nodes = flowsData.value?.flows?.nodes ?? [];
  return nodes
    .filter((f: any) => f != null)
    .map((f: any) => {
      const firstItem = f.firstItem?.nodes?.[0];
      const thumbnail =
        firstItem?.page?.document?.thumbnailDownloadUrl ??
        firstItem?.video?.thumbnailDownloadUrl ??
        null;
      return {
        id: f.id,
        title: f.title,
        thumbnail,
        pages: f.flowItems?.totalCount ?? 0,
      };
    });
});

// ── Flows already in this space (client-side "already added" hint) ──
const { data: spaceData } = useQuery<FlowPickerSpaceQuery, { id: string }>({
  query: FlowPickerSpaceDocument,
  variables: { id: props.spaceId },
  requestPolicy: "cache-and-network",
});

const locallyAddedFlowIds = ref<Set<string>>(new Set());

const existingFlowIds = computed<Set<string>>(() => {
  const sections =
    spaceData.value?.documentSpace?.documentSpaceSections.nodes ?? [];
  const ids = new Set<string>(locallyAddedFlowIds.value);
  for (const section of sections) {
    for (const item of section?.documentSpaceItemsBySectionId?.nodes ?? []) {
      if (item?.flow?.id) ids.add(item.flow.id);
    }
  }
  return ids;
});

function isAlreadyAdded(flowId: string): boolean {
  return existingFlowIds.value.has(flowId);
}

// ── Selection ──
const selection = ref<Set<string>>(new Set());

function isSelected(flowId: string): boolean {
  return selection.value.has(flowId);
}

function toggle(flowId: string) {
  if (isAlreadyAdded(flowId)) return;
  const next = new Set(selection.value);
  if (next.has(flowId)) next.delete(flowId);
  else next.add(flowId);
  selection.value = next;
}

// ── Submit ──
const { shareFlowToSpaces } = useFlowSpaceShareMutation();
const isAdding = ref(false);
const errorMessage = ref("");

function flowShareErrorMessage(
  result: Awaited<ReturnType<typeof shareFlowToSpaces>>,
): string {
  const existingSpaceNames = flowShareExistingSpaceNames(result);

  if (existingSpaceNames.length > 0) {
    return t("customFlows.shareModal.alreadySharedInSpaces", {
      spaces: existingSpaceNames.join(", "),
    });
  }

  return describeFlowShareViolation(
    (key, params) => t(key, params),
    firstBlockingFlowShareViolation(result),
  );
}

function showAddError(addedCount: number, totalCount: number, reason?: string) {
  if (addedCount > 0) {
    errorMessage.value = t("sharedUi.flowPicker.partialError", {
      added: addedCount,
      total: totalCount,
      reason: reason ?? t("sharedUi.flowPicker.addError"),
    });
    return;
  }

  errorMessage.value = reason
    ? t("sharedUi.flowPicker.addBlocked", { reason })
    : t("sharedUi.flowPicker.addError");
}

async function addSelected() {
  if (selection.value.size === 0 || isAdding.value) return;
  isAdding.value = true;
  errorMessage.value = "";
  const selectedFlowIds = Array.from(selection.value);
  let addedCount = 0;

  try {
    for (const flowId of selectedFlowIds) {
      const result = await shareFlowToSpaces(flowId, [
        { spaceId: props.spaceId, sectionId: props.sectionId },
      ]);

      if (!flowShareSucceeded(result)) {
        showAddError(
          addedCount,
          selectedFlowIds.length,
          flowShareErrorMessage(result),
        );
        return;
      }

      addedCount += 1;
      locallyAddedFlowIds.value = new Set(locallyAddedFlowIds.value).add(
        flowId,
      );
      selection.value = new Set(
        Array.from(selection.value).filter(
          (selectedId) => selectedId !== flowId,
        ),
      );
    }
    emit("added");
  } catch {
    showAddError(addedCount, selectedFlowIds.length);
  } finally {
    isAdding.value = false;
  }
}
</script>

<template>
  <OverlayModal @close="emit('close')">
    <template #header-left>
      <div>
        <h2 class="tw:text-base tw:font-semibold tw:text-label tw:m-0">
          {{ t("sharedUi.flowPicker.title") }}
        </h2>
        <p v-if="spaceName" class="tw:text-xs tw:text-label-quaternary tw:m-0">
          {{ spaceName }}
        </p>
      </div>
    </template>

    <div class="tw:flex tw:flex-col tw:flex-1 tw:min-h-0">
      <div class="tw:flex-1 tw:overflow-y-auto tw:p-4">
        <!-- Loading -->
        <div
          v-if="flowsFetching && flows.length === 0"
          class="tw:flex tw:items-center tw:justify-center tw:h-full"
        >
          <IconLoader2
            class="tw:w-6 tw:h-6 tw:text-label-quaternary tw:animate-spin"
          />
        </div>

        <!-- Load error -->
        <div
          v-else-if="flowsError"
          class="tw:flex tw:items-center tw:justify-center tw:h-full tw:text-sm tw:text-label-quaternary"
        >
          {{ t("sharedUi.flowPicker.loadError") }}
        </div>

        <!-- Empty -->
        <div
          v-else-if="flows.length === 0"
          class="tw:flex tw:items-center tw:justify-center tw:h-full tw:text-sm tw:text-label-quaternary"
        >
          {{ t("sharedUi.flowPicker.empty") }}
        </div>

        <!-- Flow list -->
        <div v-else class="tw:flex tw:flex-col">
          <div
            v-for="flow in flows"
            :key="flow.id"
            class="tw:flex tw:items-center tw:gap-3 tw:px-3 tw:py-3 tw:rounded-lg tw:transition-colors"
            :class="
              isAlreadyAdded(flow.id)
                ? 'tw:opacity-60'
                : 'tw:cursor-pointer tw:hover:bg-hover'
            "
            @click="toggle(flow.id)"
          >
            <!-- Checkbox -->
            <span
              class="tw:w-4.5 tw:h-4.5 tw:rounded tw:border-2 tw:shrink-0 tw:flex tw:items-center tw:justify-center tw:transition-colors"
              :class="
                isAlreadyAdded(flow.id)
                  ? 'tw:bg-label-faint tw:border-separator-strong'
                  : isSelected(flow.id)
                    ? 'tw:bg-control-active tw:border-control-active'
                    : 'tw:border-separator-strong'
              "
            >
              <IconCheck
                v-if="isSelected(flow.id) || isAlreadyAdded(flow.id)"
                :size="12"
                :stroke-width="3"
                class="tw:text-white"
              />
            </span>

            <!-- Thumbnail -->
            <div class="tw:relative tw:w-14 tw:h-14 tw:shrink-0">
              <img
                v-if="flow.thumbnail"
                :src="flow.thumbnail"
                class="tw:w-12 tw:h-12 tw:rounded tw:object-contain tw:bg-surface-inset tw:p-0.5"
                alt=""
              />
              <div
                v-else
                class="tw:w-12 tw:h-12 tw:rounded tw:bg-surface-inset tw:flex tw:items-center tw:justify-center"
              >
                <IconStack2
                  :size="20"
                  :stroke-width="1.5"
                  class="tw:text-label-quaternary"
                />
              </div>
            </div>

            <!-- Title + page count -->
            <div class="tw:flex-1 tw:min-w-0">
              <span
                class="tw:text-sm tw:truncate tw:block"
                :class="
                  isAlreadyAdded(flow.id)
                    ? 'tw:text-label-quaternary'
                    : 'tw:text-label-secondary'
                "
              >
                {{ flow.title }}
              </span>
              <span
                v-if="flow.pages > 0"
                class="tw:text-xs tw:text-label-quaternary"
              >
                {{
                  t("documentSearch.document.numPages", { count: flow.pages })
                }}
              </span>
            </div>

            <!-- Already added indicator -->
            <span
              v-if="isAlreadyAdded(flow.id)"
              class="tw:text-xs tw:text-label-quaternary tw:shrink-0"
            >
              {{ t("sharedUi.flowPicker.alreadyAdded") }}
            </span>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="tw:border-t tw:border-separator tw:px-4 tw:py-3 tw:flex tw:items-center tw:shrink-0"
      >
        <span v-if="errorMessage" class="tw:text-sm tw:text-destructive">{{
          errorMessage
        }}</span>
        <div class="tw:flex-1" />
        <button
          class="tw:px-4 tw:py-2 tw:rounded-lg tw:text-sm tw:font-medium tw:transition-colors tw:border-0 tw:cursor-pointer"
          :class="
            selection.size > 0 && !isAdding
              ? 'tw:bg-pv-magenta-500 tw:text-white tw:hover:bg-pv-magenta-600'
              : 'tw:bg-surface-inset tw:text-label-quaternary tw:cursor-not-allowed'
          "
          :disabled="selection.size === 0 || isAdding"
          @click="addSelected"
        >
          <IconLoader2
            v-if="isAdding"
            class="tw:w-4 tw:h-4 tw:animate-spin tw:inline tw:mr-1"
          />
          {{
            t(
              "sharedUi.flowPicker.submit",
              { count: selection.size },
              selection.size,
            )
          }}
        </button>
      </div>
    </div>
  </OverlayModal>
</template>
